import { FC, ReactElement, useEffect, useState, useRef } from "react";
import { usePspPaymentMethods } from "src/hooks/usePspPaymentMethods";
import { PspPaymentMethodsResponse } from "src/types/graphql.d";
import { PspCompany } from "src/types/psp.d";
import { PaymentMethodSelector } from "src/molecules/PaymentMethodSelector";
import qs from "query-string";
import { getIsMimCartSubtotalPriceError } from "./utils";
import { useRouter } from "next/router";
import { useRefetchStoreSettings } from "src/hooks/stores/use-app-store";
import { canHandleApplePay } from "src/utils/applepay/applePayHelper";
import {
  usePspPaymentState,
  PspPaymentStateHook,
} from "src/hooks/usePspPaymentState";
import { MolliePaymentSkeleton } from "src/molecules/MolliePaymentSkeleton";
import { CartInfo } from "src/types/cartInfo";

const MolliePayment: FC<{
  title?: ReactElement;
  cartInfo: CartInfo;
  paymentId: string | undefined;
  paymentIsInProgress: boolean;
  // payPalRef: RefObject<HTMLDivElement> | null;
  // onChange: ({ isValid }: { isValid: boolean }) => void;
  onPaymentCompleted: ({
    isPaymentPending,
  }: {
    isPaymentPending: boolean;
  }) => void;
  onPaymentRefused: () => void;
  onPaymentCancelled: () => void;
  onPaymentInitiated: (paymentId: string) => void;
  onPaymentMethodChanged: (paymentMethod: string) => void;
  // onPayPalClicked: (data: { fundingSource: string }) => void;
}> = ({
  title,
  cartInfo,
  paymentId,
  paymentIsInProgress,
  // payPalRef,
  onPaymentInitiated,
  // onChange,
  onPaymentCompleted,
  onPaymentRefused,
  onPaymentCancelled,
  onPaymentMethodChanged,
  // onPayPalClicked,
}): JSX.Element => {
  const refetchStoreSettings = useRefetchStoreSettings();
  const [loading, setLoading] = useState<boolean>(true);
  const router = useRouter();

  // If somehow paymentId state gets lost, we can try to get it from the query params again
  if (!paymentId && router.query.payment_id) {
    paymentId = router.query.payment_id as string;
  }

  const paymentState: PspPaymentStateHook | undefined = usePspPaymentState(
    paymentIsInProgress && paymentId ? paymentId : undefined
  );

  const {
    data,
    error: paymentSessionError,
    pspPaymentMethods,
  } = usePspPaymentMethods({
    refetchQueries: ["cart"],
    onCompleted: (data: { pspPaymentMethods: PspPaymentMethodsResponse }) => {
      paymentId = data.pspPaymentMethods.payment_id;
      onPaymentInitiated(data.pspPaymentMethods.payment_id);

      setLoading(false);
    },
  });

  useEffect(() => {
    if (paymentSessionError) {
      const isMimCartSubtotalPriceError = getIsMimCartSubtotalPriceError(
        paymentSessionError.graphQLErrors[0]?.message
      );

      if (isMimCartSubtotalPriceError && refetchStoreSettings) {
        void refetchStoreSettings();
      }

      const { query } = qs.parseUrl(window.location.href);
      void router.push({
        pathname: "/checkout",
        query: {
          ...query,
          step: 1,
        },
      });
    }
  }, [refetchStoreSettings, paymentSessionError, router]);

  const pspPaymentMethodsLoadedRef = useRef(false);

  useEffect(() => {
    if (
      (paymentIsInProgress && !data) ||
      !cartInfo.id ||
      cartInfo.version === undefined ||
      cartInfo.version === null ||
      pspPaymentMethodsLoadedRef.current
    ) {
      return;
    }

    pspPaymentMethods({
      psp: PspCompany.Mollie,
      applepay: canHandleApplePay(),
      cartInfo,
    });

    pspPaymentMethodsLoadedRef.current = true;
  }, [data, cartInfo, pspPaymentMethods, paymentIsInProgress]);

  useEffect(() => {
    if (paymentState?.loading) {
      return;
    }

    if (paymentState?.error) {
      // Refuse payment if there's an error. Chances are that something is up with the payment state check.
      onPaymentRefused();
      return;
    }

    if (!paymentState?.paymentStateData) {
      return;
    }

    switch (paymentState.paymentStateData.state) {
      case "Success":
        onPaymentCompleted({ isPaymentPending: false });
        break;

      case "Failure":
        onPaymentRefused();
        break;

      case "Cancelled":
        onPaymentCancelled();
        break;

      case "Pending":
        if (paymentState.paymentStateData.method === "banktransfer") {
          // Banktransfers will always stay pending until it has been paid. So let's continue with the pending message
          onPaymentCompleted({ isPaymentPending: true });
        }
        break;

      default:
        // Do nothing
        break;
    }
  }, [
    paymentState?.loading,
    paymentState?.error,
    paymentState?.paymentStateData,
    onPaymentCompleted,
    onPaymentRefused,
    onPaymentCancelled,
  ]);

  return (
    <>
      {loading && <MolliePaymentSkeleton />}
      {title && !loading && title}
      <PaymentMethodSelector
        paymentMethods={data?.pspPaymentMethods.payment_methods || []}
        onPaymentMethodSelected={onPaymentMethodChanged}
      />
    </>
  );
};

export default MolliePayment;
